import type { DocumentData } from 'firebase/firestore';
import { AlertTriangle } from 'lucide-react';
import { Markdown } from '../Markdown';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertBanner } from '@/components/AlertBanner';
import { useT } from '@/lib/i18n';
import type { messages as MSG } from '@/lib/i18n.messages';

type SummaryKey = '00_executive_summary' | 'ai_readiness' | '09_preconisations'
  | '08_data_catalog' | 'checklist' | '07_filesystem_map';

/**
 * Rapports Markdown du pipeline (reports/*.md) affichés dans l'onglet
 * « Résumé », dans cet ordre. Le contrat 1.2 en ajoute deux :
 * `09_preconisations` (le récit du livrable, dont les onglets « Préconisations »
 * donnent la version chiffrée) et `08_data_catalog`. Un audit 1.0 / 1.1 ne les
 * porte pas : ils sont simplement absents de la liste rendue.
 */
export const SUMMARY_REPORT_KEYS: readonly SummaryKey[] = [
  '00_executive_summary', 'ai_readiness', '09_preconisations', '08_data_catalog',
  'checklist', '07_filesystem_map',
];

const TITLE_KEY: Record<SummaryKey, keyof typeof MSG> = {
  '00_executive_summary': 'audit.summary.00_executive_summary',
  ai_readiness: 'audit.summary.ai_readiness',
  '09_preconisations': 'audit.tab.recommendations',
  '08_data_catalog': 'audit.tab.data_catalog',
  checklist: 'audit.summary.checklist',
  '07_filesystem_map': 'audit.summary.07_filesystem_map',
};

/** Clés des rapports de synthèse présents (reportMd non vide), dans l'ordre d'affichage. */
export function availableSummaryReports(modules: Record<string, DocumentData | undefined>): SummaryKey[] {
  return SUMMARY_REPORT_KEYS.filter(key => typeof modules[key]?.reportMd === 'string' && modules[key]!.reportMd.trim() !== '');
}

/**
 * Onglet « Résumé » : résumé exécutif, AI Readiness, checklist et rapport de
 * cartographie, tels que produits par le toolkit (modules/{key}.reportMd).
 * Aucun appel réseau : les rapports sont déjà inline dans la sous-collection.
 */
export function SummaryTab({ modules }: { modules: Record<string, DocumentData | undefined> }) {
  const { t } = useT();
  const keys = availableSummaryReports(modules);
  if (keys.length === 0) {
    return <Card><div className="text-sm text-ink-500">{t('audit.summary.empty')}</div></Card>;
  }
  return (
    <div className="space-y-6">
      {keys.map(key => {
        const mod = modules[key]!;
        const failed = mod.status === 'failed' || mod.status === 'interrupted';
        return (
          <Card key={key} id={`summary-${key}`} className="scroll-mt-24">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="font-display text-lg text-ink-900 dark:text-white">{t(TITLE_KEY[key])}</h2>
              {failed && <Badge variant="danger">{t('audit.module.status.failed')}</Badge>}
              <span className="ml-auto text-xs text-ink-500">{key}</span>
            </div>
            {failed && (
              <AlertBanner variant="warning" icon={<AlertTriangle className="h-4 w-4" />} title={t('audit.summary.pipeline_failed')}>
                <span />
              </AlertBanner>
            )}
            <Markdown source={mod.reportMd as string} />
          </Card>
        );
      })}
    </div>
  );
}
